import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  FaCloudUploadAlt,
  FaCopy,
  FaDownload,
  FaImage,
  FaRedo,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import {
  deleteWordpressMedia,
  downloadWordpressMediaFile,
  formatWordpressFieldsForCopy,
  getWordpressMediaBlob,
  isAcceptedImageFile,
  listWordpressMedia,
  purgeWordpressMedia,
  regenerateWordpressMediaSeo,
  saveImageBlobLocally,
  saveOverlayToLibrary,
  updateWordpressMediaSeo,
  uploadWordpressMedia,
  WordpressMediaRow,
} from '../services/wordpressMedia.service';
import {
  createKeystoneOverlay,
  DEFAULT_BRAND_LOGO_SRC,
  DEFAULT_OVERLAY_PLACEMENT,
  DEFAULT_OVERLAY_PRIMARY_LABEL,
  DEFAULT_OVERLAY_SECONDARY_LABEL,
  OVERLAY_GRID,
  OverlayPlacement,
  OverlaySlot,
  overlaySlotLabel,
} from '../services/imageOverlay';

type PendingItem = {
  id: string;
  file: File;
  previewUrl: string;
  status: 'ready' | 'working' | 'error';
  error?: string;
};

type OverlayResult = OverlayPlacement & {
  url: string;
  blob: Blob;
  filename: string;
  width: number;
  height: number;
};

type SeoDraft = {
  filename: string;
  title: string;
  altText: string;
  caption: string;
  description: string;
};

function formatBytes(bytes?: number | null) {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
}

function wordpressMediaPreviewSrc(row: Pick<WordpressMediaRow, 'public_url' | 'updated_at' | 'created_at'>) {
  const url = row.public_url || '';
  const stamp = row.updated_at || row.created_at || '';
  if (!url || !stamp) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(stamp)}`;
}

function CoveringFileInput({
  accept,
  multiple,
  onFiles,
}: {
  accept: string;
  multiple?: boolean;
  onFiles: (files: FileList) => void;
}) {
  const restoreWindow = () => {
    window.setTimeout(() => window.focus(), 400);
  };

  return (
    <input
      type="file"
      accept={accept}
      multiple={multiple}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => {
        if (event.target.files?.length) onFiles(event.target.files);
        event.target.value = '';
        restoreWindow();
      }}
      onBlur={restoreWindow}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        cursor: 'pointer',
        fontSize: 0,
      }}
    />
  );
}

const ImagePurifyPage: React.FC = () => {
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [items, setItems] = useState<WordpressMediaRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, SeoDraft>>({});
  const [keywords, setKeywords] = useState('');
  const [regenPrompts, setRegenPrompts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [purging, setPurging] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const saveTimers = useRef<Record<string, number>>({});
  const saveTokens = useRef<Record<string, number>>({});
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [overlayLibraryId, setOverlayLibraryId] = useState<string | null>(null);
  const [overlayFile, setOverlayFile] = useState<File | null>(null);
  const [overlayFilePreview, setOverlayFilePreview] = useState<string | null>(null);
  const [overlayPrimary, setOverlayPrimary] = useState(DEFAULT_OVERLAY_PRIMARY_LABEL);
  const [overlaySecondary, setOverlaySecondary] = useState(DEFAULT_OVERLAY_SECONDARY_LABEL);
  const [overlaySlot, setOverlaySlot] = useState<OverlaySlot>(DEFAULT_OVERLAY_PLACEMENT.position);
  const [overlayLogoFile, setOverlayLogoFile] = useState<File | null>(null);
  const [overlaying, setOverlaying] = useState(false);
  const [overlayDragOver, setOverlayDragOver] = useState(false);
  const [overlayResult, setOverlayResult] = useState<OverlayResult | null>(null);
  const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(null);

  const loadLibrary = useCallback(async () => {
    try {
      setError(null);
      const rows = await listWordpressMedia();
      setItems(rows);
      setDrafts(Object.fromEntries(rows.map((row) => [row.id, {
        filename: row.webp_filename || '',
        title: row.title || '',
        altText: row.alt_text || '',
        caption: row.caption || '',
        description: row.description || '',
      }])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load WordPress media');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  useEffect(() => {
    return () => {
      pending.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
    // Only revoke leftover object URLs on unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList: FileList | File[]) => {
    const next: PendingItem[] = [];
    Array.from(fileList).forEach((file) => {
      if (!isAcceptedImageFile(file)) {
        setError(`Skipped ${file.name}: use JPEG, PNG, WebP, GIF or BMP.`);
        return;
      }
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        previewUrl: URL.createObjectURL(file),
        status: 'ready',
      });
    });
    if (next.length) {
      setPending((current) => [...current, ...next]);
      setError(null);
    }
  };

  const removePending = (id: string) => {
    setPending((current) => {
      const match = current.find((item) => item.id === id);
      if (match) URL.revokeObjectURL(match.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  };

  const handleGenerate = async () => {
    const queue = pending.filter((item) => item.status !== 'working');
    if (!queue.length) {
      setError('Add at least one image before generating.');
      return;
    }

    setGenerating(true);
    setError(null);
    const seoFailures: string[] = [];

    for (const item of queue) {
      setPending((current) => current.map((entry) => (
        entry.id === item.id ? { ...entry, status: 'working', error: undefined } : entry
      )));
      try {
        const { seoError } = await uploadWordpressMedia({
          file: item.file,
          keywords,
        });
        if (seoError) seoFailures.push(`${item.file.name}: ${seoError}`);
        URL.revokeObjectURL(item.previewUrl);
        setPending((current) => current.filter((entry) => entry.id !== item.id));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to convert image';
        setPending((current) => current.map((entry) => (
          entry.id === item.id ? { ...entry, status: 'error', error: message } : entry
        )));
      }
    }

    await loadLibrary();
    setGenerating(false);
    if (seoFailures.length) {
      setError(seoFailures.join(' '));
      setNotice('Images were converted to WebP. SEO fields could not be generated for one or more files.');
    } else {
      setNotice('Images converted to WebP and SEO fields generated.');
    }
  };

  const handlePurge = async () => {
    if (!items.length) return;
    if (!window.confirm('Purge all Image Purify images and their SEO fields from the database? This cannot be undone.')) {
      return;
    }
    try {
      setPurging(true);
      setError(null);
      const deleted = await purgeWordpressMedia();
      setItems([]);
      setDrafts({});
      setNotice(deleted === 1 ? 'Purged 1 image.' : `Purged ${deleted} images.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to purge Image Purify images');
    } finally {
      setPurging(false);
    }
  };

  const copyText = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setNotice(`${label} copied`);
  };

  const cancelPendingSave = (id: string) => {
    window.clearTimeout(saveTimers.current[id]);
    delete saveTimers.current[id];
    saveTokens.current[id] = (saveTokens.current[id] || 0) + 1;
  };

  const saveDraftNow = async (id: string, draft: SeoDraft, token: number) => {
    try {
      setSavingId(id);
      const updated = await updateWordpressMediaSeo(id, draft);
      if (saveTokens.current[id] !== token) return;
      setItems((current) => current.map((item) => (item.id === id ? updated : item)));
    } catch (err) {
      if (saveTokens.current[id] !== token) return;
      setError(err instanceof Error ? err.message : 'Failed to save SEO fields');
    } finally {
      if (saveTokens.current[id] === token) setSavingId((current) => (current === id ? null : current));
    }
  };

  const scheduleDraftSave = (id: string, draft: SeoDraft) => {
    window.clearTimeout(saveTimers.current[id]);
    const token = (saveTokens.current[id] || 0) + 1;
    saveTokens.current[id] = token;
    saveTimers.current[id] = window.setTimeout(() => {
      void saveDraftNow(id, draft, token);
    }, 700);
  };

  const updateDraftField = (row: WordpressMediaRow, key: keyof SeoDraft, value: string) => {
    const next = {
      ...(drafts[row.id] || {
        filename: row.webp_filename || '',
        title: row.title || '',
        altText: row.alt_text || '',
        caption: row.caption || '',
        description: row.description || '',
      }),
      [key]: value,
    };
    setDrafts((current) => ({ ...current, [row.id]: next }));
    scheduleDraftSave(row.id, next);
  };

  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const regenerate = async (row: WordpressMediaRow) => {
    try {
      cancelPendingSave(row.id);
      setRegeneratingId(row.id);
      const extraPrompt = (regenPrompts[row.id] || '').trim() || keywords.trim() || undefined;
      const updated = await regenerateWordpressMediaSeo(row, extraPrompt);
      setItems((current) => current.map((item) => (item.id === row.id ? updated : item)));
      setDrafts((current) => ({
        ...current,
        [row.id]: {
          filename: updated.webp_filename || '',
          title: updated.title || '',
          altText: updated.alt_text || '',
          caption: updated.caption || '',
          description: updated.description || '',
        },
      }));
      setNotice('SEO fields regenerated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate SEO fields');
    } finally {
      setRegeneratingId(null);
    }
  };

  const removeItem = async (row: WordpressMediaRow) => {
    if (!window.confirm(`Delete ${row.webp_filename}?`)) return;
    try {
      cancelPendingSave(row.id);
      await deleteWordpressMedia(row);
      setItems((current) => current.filter((item) => item.id !== row.id));
      setSelectedLibraryId((current) => (current === row.id ? null : current));
      setOverlayLibraryId((current) => (current === row.id ? null : current));
      setNotice('Media deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete media');
    }
  };

  const downloadWebp = async (row: WordpressMediaRow) => {
    try {
      await downloadWordpressMediaFile({
        ...row,
        webp_filename: row.webp_filename,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download image');
    }
  };

  const setOverlayUpload = (file: File | null) => {
    if (overlayFilePreview) URL.revokeObjectURL(overlayFilePreview);
    setOverlayFile(file);
    setOverlayFilePreview(file ? URL.createObjectURL(file) : null);
    if (file) setOverlayLibraryId(null);
  };

  const chooseLibraryImage = (row: WordpressMediaRow) => {
    setOverlayUpload(null);
    setOverlayLibraryId(row.id);
    document.getElementById('image-overlay')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOverlay = async () => {
    const libraryRow = overlayLibraryId ? items.find((item) => item.id === overlayLibraryId) : undefined;
    if (!overlayFile && !libraryRow) {
      setError('Choose a library image or upload a photo for the overlay.');
      return;
    }
    try {
      setOverlaying(true);
      setError(null);
      const source = overlayFile || await getWordpressMediaBlob(libraryRow!);
      const rendered = await createKeystoneOverlay({
        source,
        primaryLabel: overlayPrimary,
        secondaryLabel: overlaySecondary,
        brandLogo: overlayLogoFile || DEFAULT_BRAND_LOGO_SRC,
        position: overlaySlot,
        originalFilename: overlayFile?.name || libraryRow?.webp_filename || libraryRow?.original_filename,
      });
      const saved = await saveOverlayToLibrary({
        blob: rendered.blob,
        filename: rendered.filename,
        width: rendered.width,
        height: rendered.height,
        originalFilename: overlayFile?.name || libraryRow?.original_filename || rendered.filename,
        overlay: {
          sourceMediaId: libraryRow?.id || null,
          primaryLabel: rendered.primaryLabel,
          secondaryLabel: rendered.secondaryLabel,
          infoPosition: rendered.position,
          logoPosition: rendered.position,
        },
      });
      if (overlayResult?.url) URL.revokeObjectURL(overlayResult.url);
      setOverlayResult({
        url: saved.row.public_url,
        blob: rendered.blob,
        filename: saved.row.webp_filename || rendered.filename,
        width: saved.row.width || rendered.width,
        height: saved.row.height || rendered.height,
        position: rendered.position,
        primaryLabel: rendered.primaryLabel,
        secondaryLabel: rendered.secondaryLabel,
      });
      await loadLibrary();
      if (saved.seoError) {
        setError(saved.seoError);
        setNotice('Overlay saved to the library. SEO fields could not be generated.');
      } else {
        setNotice('Overlay saved to the library');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply overlay');
    } finally {
      setOverlaying(false);
    }
  };

  const downloadOverlay = async () => {
    if (!overlayResult) return;
    try {
      await saveImageBlobLocally(overlayResult.blob, overlayResult.filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download overlay');
    }
  };

  const overlaySourcePreview = overlayFilePreview || items.find((item) => item.id === overlayLibraryId)?.public_url || null;
  const overlaySourceName = overlayFile?.name
    || items.find((item) => item.id === overlayLibraryId)?.webp_filename
    || '';
  const selectedLibraryRow = items.find((item) => item.id === selectedLibraryId) || null;

  const readyCount = useMemo(
    () => pending.filter((item) => item.status !== 'working').length,
    [pending]
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography level="h2">Image Purify</Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Convert images to an optimised WebP at original size, then generate factual WordPress filename, Title, Alt Text, Caption and Description.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="lg"
            variant="outlined"
            color="danger"
            disabled={!items.length || generating || purging}
            loading={purging}
            startDecorator={<IconWrapper icon={FaTrash} />}
            onClick={handlePurge}
          >
            Purge
          </Button>
          <Button
            size="lg"
            disabled={!readyCount || generating || purging}
            loading={generating}
            onClick={handleGenerate}
          >
            Generate
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Stack spacing={2}>
          <FormControl>
            <FormLabel>Additional prompt (optional)</FormLabel>
            <Textarea
              minRows={2}
              value={keywords}
              onChange={(event) => setKeywords(event.target.value)}
              placeholder="Default location is Chichester, West Sussex. Add extra instruction or another town to override it."
            />
          </FormControl>

          <Box
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              if (event.dataTransfer.files?.length) addFiles(event.dataTransfer.files);
            }}
            sx={{
              position: 'relative',
              display: 'block',
              border: '2px dashed',
              borderColor: dragOver ? 'primary.outlinedBorder' : 'neutral.outlinedBorder',
              backgroundColor: dragOver ? 'primary.softBg' : 'background.level1',
              borderRadius: 'md',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <IconWrapper icon={FaCloudUploadAlt} style={{ fontSize: 28 }} />
              <Typography level="title-md">Drop images here, or click to choose</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                JPEG, PNG, WebP, GIF or BMP. Converted to WebP at original dimensions, then SEO fields are generated.
              </Typography>
            </Stack>
            <CoveringFileInput
              accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
              multiple
              onFiles={addFiles}
            />
          </Box>

          {pending.length > 0 && (
            <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
              {pending.map((item) => (
                <Card key={item.id} variant="soft" sx={{ minWidth: 160, maxWidth: 180, p: 1 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={item.previewUrl}
                      alt={item.file.name}
                      sx={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 'sm' }}
                    />
                    {item.status !== 'working' && (
                      <IconButton
                        size="sm"
                        variant="solid"
                        color="neutral"
                        onClick={(event) => {
                          event.stopPropagation();
                          removePending(item.id);
                        }}
                        sx={{ position: 'absolute', top: 4, right: 4 }}
                      >
                        <IconWrapper icon={FaTimes} />
                      </IconButton>
                    )}
                    {item.status === 'working' && (
                      <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        borderRadius: 'sm',
                      }}>
                        <CircularProgress size="sm" />
                      </Box>
                    )}
                  </Box>
                  <Typography level="body-xs" sx={{ mt: 0.75 }} noWrap title={item.file.name}>
                    {item.file.name}
                  </Typography>
                  {item.error && (
                    <Typography level="body-xs" color="danger">{item.error}</Typography>
                  )}
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Typography level="h3" sx={{ mb: 1 }}>Library</Typography>
      <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 2 }}>
        Click a thumbnail to open SEO fields, copy/download, regenerate or send it to Image Overlay.
      </Typography>

      {loading ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
          <CircularProgress />
          <Typography level="body-sm">Loading media…</Typography>
        </Stack>
      ) : items.length === 0 ? (
        <Alert color="neutral">No converted images yet. Upload files and click Generate.</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(3, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(6, minmax(0, 1fr))',
              lg: 'repeat(8, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {items.map((row) => {
            const draft = drafts[row.id] || {
              filename: row.webp_filename,
              title: row.title,
              altText: row.alt_text,
            };
            return (
              <Box
                key={row.id}
                component="button"
                type="button"
                onClick={() => setSelectedLibraryId(row.id)}
                sx={{
                  position: 'relative',
                  display: 'block',
                  p: 0,
                  border: '2px solid',
                  borderColor: selectedLibraryId === row.id || overlayLibraryId === row.id
                    ? 'primary.solidBg'
                    : 'neutral.outlinedBorder',
                  borderRadius: 'sm',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'none',
                }}
              >
                <Box
                  component="img"
                  src={wordpressMediaPreviewSrc(row)}
                  alt={draft.altText || row.webp_filename}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    objectFit: 'cover',
                    display: 'block',
                    backgroundColor: 'neutral.softBg',
                  }}
                />
                {row.is_overlay && (
                  <Chip
                    size="sm"
                    variant="solid"
                    color="primary"
                    sx={{ position: 'absolute', left: 4, bottom: 4 }}
                  >
                    Overlay
                  </Chip>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Modal open={!!selectedLibraryRow} onClose={() => setSelectedLibraryId(null)}>
        <ModalDialog sx={{ width: { xs: '96vw', md: 640 }, maxWidth: 720, maxHeight: '92vh', p: 2 }}>
          <ModalClose />
          {selectedLibraryRow && (() => {
            const row = selectedLibraryRow;
            const draft = drafts[row.id] || {
              filename: row.webp_filename,
              title: row.title,
              altText: row.alt_text,
              caption: row.caption,
              description: row.description,
            };
            return (
              <>
                <DialogTitle sx={{ pr: 4 }}>{draft.filename || row.webp_filename}</DialogTitle>
                <DialogContent sx={{ overflow: 'auto' }}>
                  <Stack spacing={1.25}>
                    <Box
                      component="img"
                      src={wordpressMediaPreviewSrc(row)}
                      alt={draft.altText || row.webp_filename}
                      sx={{
                        width: '100%',
                        maxHeight: 280,
                        objectFit: 'contain',
                        borderRadius: 'sm',
                        backgroundColor: 'neutral.softBg',
                      }}
                    />
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip size="sm" variant="soft">WebP</Chip>
                      {row.is_overlay && (
                        <Chip size="sm" variant="soft" color="primary">Overlay</Chip>
                      )}
                      {row.width && row.height && (
                        <Chip size="sm" variant="soft">{row.width} × {row.height}</Chip>
                      )}
                      <Chip size="sm" variant="soft">{formatBytes(row.file_size_bytes)}</Chip>
                    </Stack>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {formatDate(row.created_at)}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<IconWrapper icon={FaCopy} />}
                        onClick={() => copyText(formatWordpressFieldsForCopy({
                          ...row,
                          webp_filename: draft.filename,
                          title: draft.title,
                          alt_text: draft.altText,
                          caption: draft.caption,
                          description: draft.description,
                        }), 'WordPress fields')}
                      >
                        Copy all
                      </Button>
                      <Button
                        size="sm"
                        variant="outlined"
                        onClick={() => copyText(row.public_url, 'Image URL')}
                      >
                        Copy URL
                      </Button>
                      <Tooltip title="Use for overlay">
                        <IconButton
                          size="sm"
                          variant={overlayLibraryId === row.id ? 'solid' : 'outlined'}
                          onClick={() => {
                            chooseLibraryImage(row);
                            setSelectedLibraryId(null);
                          }}
                        >
                          <IconWrapper icon={FaImage} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download WebP">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          onClick={() => downloadWebp({
                            ...row,
                            webp_filename: draft.filename || row.webp_filename,
                          })}
                        >
                          <IconWrapper icon={FaDownload} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={() => removeItem(row)}
                        >
                          <IconWrapper icon={FaTrash} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <FormControl size="sm">
                      <FormLabel>Regenerate prompt (optional)</FormLabel>
                      <Textarea
                        minRows={2}
                        value={regenPrompts[row.id] || ''}
                        onChange={(event) => setRegenPrompts((current) => ({
                          ...current,
                          [row.id]: event.target.value,
                        }))}
                        placeholder="Override Chichester, West Sussex or add extra instruction for this image only."
                      />
                    </FormControl>
                    {([
                      { key: 'filename', label: 'Filename', rows: 1, disabled: true },
                      { key: 'altText', label: 'Alt Text', rows: 2, disabled: false },
                      { key: 'title', label: 'Title', rows: 1, disabled: false },
                      { key: 'caption', label: 'Caption', rows: 2, disabled: false },
                      { key: 'description', label: 'Description', rows: 2, disabled: false },
                    ] as const).map((field) => (
                      <FormControl key={field.key} size="sm">
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <FormLabel>{field.label}</FormLabel>
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => copyText(draft[field.key], field.label)}
                          >
                            <IconWrapper icon={FaCopy} />
                          </IconButton>
                        </Stack>
                        {field.rows === 1 ? (
                          <Input
                            disabled={field.disabled}
                            value={draft[field.key]}
                            onChange={(event) => updateDraftField(row, field.key, event.target.value)}
                            sx={field.disabled ? { bgcolor: 'neutral.softBg', color: 'text.tertiary' } : undefined}
                          />
                        ) : (
                          <Textarea
                            disabled={field.disabled}
                            minRows={field.rows}
                            value={draft[field.key]}
                            onChange={(event) => updateDraftField(row, field.key, event.target.value)}
                            sx={field.disabled ? { bgcolor: 'neutral.softBg', color: 'text.tertiary' } : undefined}
                          />
                        )}
                      </FormControl>
                    ))}
                    <Stack spacing={0.5}>
                      <Button
                        type="button"
                        size="sm"
                        variant="outlined"
                        loading={regeneratingId === row.id}
                        startDecorator={<IconWrapper icon={FaRedo} />}
                        onClick={() => regenerate(row)}
                      >
                        Regenerate SEO
                      </Button>
                      {savingId === row.id && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                          Saving…
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </DialogContent>
              </>
            );
          })()}
        </ModalDialog>
      </Modal>

      <Box id="image-overlay" sx={{ mt: 5 }}>
        <Typography level="h3" sx={{ mb: 1 }}>Image Overlay</Typography>
        <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 2 }}>
          Keep the original photograph unchanged and stamp the Keystone badge onto it. Choose a library image or upload a new photograph, pick a 3×3 cell, and edit the primary and secondary labels on the SVG. No AI is used.
        </Typography>

        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>Use a library image (optional)</Typography>
              {items.length === 0 ? (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Convert images in Image Purify above, then select one here instead of uploading.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                  {items.map((row) => (
                    <Box
                      key={`overlay-${row.id}`}
                      component="button"
                      type="button"
                      onClick={() => chooseLibraryImage(row)}
                      sx={{
                        border: '2px solid',
                        borderColor: overlayLibraryId === row.id ? 'primary.solidBg' : 'neutral.outlinedBorder',
                        borderRadius: 'sm',
                        p: 0,
                        background: 'none',
                        cursor: 'pointer',
                        flex: '0 0 auto',
                      }}
                    >
                      <Box
                        component="img"
                        src={wordpressMediaPreviewSrc(row)}
                        alt={row.webp_filename}
                        sx={{ width: 88, height: 64, objectFit: 'cover', display: 'block', borderRadius: 'xs' }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box
              onDragOver={(event) => {
                event.preventDefault();
                setOverlayDragOver(true);
              }}
              onDragLeave={() => setOverlayDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setOverlayDragOver(false);
                const file = event.dataTransfer.files?.[0];
                if (file && isAcceptedImageFile(file)) setOverlayUpload(file);
              }}
              sx={{
                position: 'relative',
                display: 'block',
                border: '2px dashed',
                borderColor: overlayDragOver ? 'primary.outlinedBorder' : 'neutral.outlinedBorder',
                backgroundColor: overlayDragOver ? 'primary.softBg' : 'background.level1',
                borderRadius: 'md',
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
              }}
            >
              <Stack alignItems="center" spacing={0.75}>
                <IconWrapper icon={FaCloudUploadAlt} style={{ fontSize: 22 }} />
                <Typography level="title-sm">Or upload a new photograph</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Original size and composition are preserved. No crop or enhancement.
                </Typography>
              </Stack>
              <CoveringFileInput
                accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
                onFiles={(files) => {
                  const file = files[0];
                  if (file && isAcceptedImageFile(file)) setOverlayUpload(file);
                }}
              />
            </Box>

            {(overlaySourcePreview || overlaySourceName) && (
              <Stack spacing={1.25}>
                {overlaySourcePreview ? (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: 720,
                      borderRadius: 'sm',
                      overflow: 'hidden',
                      backgroundColor: 'neutral.softBg',
                    }}
                  >
                    <Box
                      component="img"
                      src={overlaySourcePreview}
                      alt=""
                      sx={{ width: '100%', display: 'block', verticalAlign: 'middle' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gridTemplateRows: '1fr 1fr 1fr',
                      }}
                    >
                      {OVERLAY_GRID.flat().map((slot) => {
                        const selected = overlaySlot === slot;
                        return (
                          <Box
                            key={slot}
                            component="button"
                            type="button"
                            onClick={() => setOverlaySlot(slot)}
                            aria-label={`Place lockup ${overlaySlotLabel(slot)}`}
                            sx={{
                              margin: 0,
                              border: '1px solid',
                              borderColor: selected ? 'primary.solidBg' : 'rgba(255,255,255,0.55)',
                              backgroundColor: selected ? 'rgba(33,45,59,0.38)' : 'rgba(15,23,32,0.08)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              '&:hover': {
                                backgroundColor: 'rgba(33,45,59,0.28)',
                              },
                            }}
                          >
                            {selected && (
                              <Chip size="sm" variant="solid" color="primary">
                                Lockup
                              </Chip>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Typography level="body-sm">{overlaySourceName || 'Selected photograph'}</Typography>
                )}
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {overlaySourceName} · click a cell to place the lockup ({overlaySlotLabel(overlaySlot)})
                </Typography>
              </Stack>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Primary label</FormLabel>
                <Input
                  value={overlayPrimary}
                  onChange={(event) => setOverlayPrimary(event.target.value)}
                  placeholder={DEFAULT_OVERLAY_PRIMARY_LABEL}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Secondary label</FormLabel>
                <Input
                  value={overlaySecondary}
                  onChange={(event) => setOverlaySecondary(event.target.value)}
                  placeholder={DEFAULT_OVERLAY_SECONDARY_LABEL}
                />
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>Brand logo</FormLabel>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Chip size="sm" variant="soft">keystone-overlay.svg</Chip>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Button size="sm" variant="outlined" component="span" sx={{ pointerEvents: 'none' }}>
                    Replace logo
                  </Button>
                  <CoveringFileInput
                    accept="image/png,image/webp,image/svg+xml,image/jpeg"
                    onFiles={(files) => setOverlayLogoFile(files[0] || null)}
                  />
                </Box>
                {overlayLogoFile && (
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {overlayLogoFile.name}
                  </Typography>
                )}
              </Stack>
            </FormControl>

            <Button
              loading={overlaying}
              disabled={overlaying || (!overlayFile && !overlayLibraryId)}
              onClick={handleOverlay}
            >
              Apply overlay
            </Button>

            {overlayResult && (
              <Stack spacing={1.25}>
                <Box
                  component="img"
                  src={overlayResult.url}
                  alt={overlayResult.filename}
                  sx={{
                    width: '100%',
                    maxHeight: 520,
                    objectFit: 'contain',
                    borderRadius: 'sm',
                    backgroundColor: 'neutral.softBg',
                  }}
                />
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="sm" variant="soft">{overlaySlotLabel(overlayResult.position)}</Chip>
                  <Chip size="sm" variant="soft">{overlayResult.primaryLabel}</Chip>
                  <Chip size="sm" variant="soft">{overlayResult.secondaryLabel}</Chip>
                  {overlayResult.width && overlayResult.height && (
                    <Chip size="sm" variant="soft">{overlayResult.width} × {overlayResult.height}</Chip>
                  )}
                </Stack>
                <Button
                  variant="outlined"
                  startDecorator={<IconWrapper icon={FaDownload} />}
                  onClick={downloadOverlay}
                >
                  Save overlay
                </Button>
              </Stack>
            )}
          </Stack>
        </Card>
      </Box>

      <Snackbar
        open={!!notice}
        variant="soft"
        color="success"
        onClose={() => setNotice(null)}
        autoHideDuration={3500}
      >
        {notice}
      </Snackbar>
    </Box>
  );
};

export default ImagePurifyPage;
